<template>
  <BModal
    modal-name="bulkUpdateFreeTags"
    width="550px"
    height="490px"
    :delegate="modalDelegate"
  >
    <BHeightAdjuster>
      <BModalHeader
        center
        class="modal-header"
        :title="$t('freeTag.bulkUpdate.title')"
        @modal-close="handleClose"
      />
      <BModalBody class="modal-body">
        <BulkUpdateModalTarget
          :label="$t('freeTag.bulkUpdate.target')"
          :count="selectedCallTargetIds.length"
        />
        <BListItem class="mb-300">
          <template #header>
            <div>{{ $t('freeTag.bulkUpdate.modeLabel') }}</div>
          </template>
          <BLayout align-center>
            <BRadio
              v-model="mode"
              label="add"
              class="mr-300"
            >
              {{ $t('freeTag.bulkUpdate.modeAdd') }}
            </BRadio>
            <BRadio
              v-model="mode"
              label="delete"
            >
              {{ $t('freeTag.bulkUpdate.modeDelete') }}
            </BRadio>
          </BLayout>
        </BListItem>
        <BListItem>
          <template #header>
            <div>{{ $t('freeTag.bulkUpdate.selectLabel') }}</div>
          </template>
          <BSelect
            v-model="selectedFreeTagIds"
            class="free-tag-select"
            :items="freeTagsCandidates"
            item-text="label"
            item-value="value"
            :placeholder="$t('freeTag.bulkUpdate.selectPlaceholder')"
            multiple
            filterable
          />
        </BListItem>
        <div
          v-if="mode === 'add'"
          class="note mt-200"
        >
          {{ $t('freeTag.bulkUpdate.noteAdd') }}
        </div>
        <BulkUpdateOverLimitMessage
          :visible="isOverLimit"
          :limit="BULK_UPDATE_LIMIT"
        />
      </BModalBody>
      <BulkUpdateModalFooter
        :loading="isLoading"
        :disabled="isUpdateDisabled"
        @cancel="handleClose"
        @submit="submitBulkUpdateFreeTags"
      />
    </BHeightAdjuster>
  </BModal>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { CallTargetApiService } from '@/api/user/resources/call_target';
import BulkUpdateModalFooter from '@/components/organisms/user/leadList/BulkUpdateModalFooter.vue';
import BulkUpdateModalTarget from '@/components/organisms/user/leadList/BulkUpdateModalTarget.vue';
import BulkUpdateOverLimitMessage from '@/components/organisms/user/leadList/BulkUpdateOverLimitMessage.vue';
import { useConfirm } from '@/composable/message-dialog';
import { useVuexFreeTags } from '@/composable/user/freeTag/free-tag';
import { useWait } from '@/composable/vue-wait';
import { KEY_OF_MODAL, KEY_OF_TOAST } from '@/injection-keys';
import { key } from '@/store/index';

type TProps = {
  selectedCallTargetIds: number[];
};
type TMode = 'add' | 'delete';

const props = defineProps<TProps>();

const MODAL_NAME = 'bulkUpdateFreeTags';
const BULK_UPDATE_LIMIT = 100;
const i18n = useI18n();
const store = useStore(key);
const modal = inject(KEY_OF_MODAL);
const toast = inject(KEY_OF_TOAST);
const { wait, doActionWithWait } = useWait();
const { confirm } = useConfirm();
const { freeTagsCandidates, fetchAll } = useVuexFreeTags();
const api = new CallTargetApiService();

const mode = ref<TMode>('add');
const selectedFreeTagIds = ref<number[]>([]);

onMounted(() => {
  fetchAll();
});

const isOverLimit = computed<boolean>(() => props.selectedCallTargetIds.length > BULK_UPDATE_LIMIT);
const isUpdateDisabled = computed<boolean>(() => selectedFreeTagIds.value.length === 0 || isOverLimit.value);
const isLoading = computed<boolean>(() => wait.is(['bulkUpdateFreeTagsWait']));

const modalDelegate = {
  beforeClose: () => {
    mode.value = 'add';
    selectedFreeTagIds.value = [];
  },
};

const handleClose = () => {
  modal.hide(MODAL_NAME);
};

const selectedFreeTagNames = computed<string>(() =>
  freeTagsCandidates.value
    .filter((candidate) => selectedFreeTagIds.value.includes(candidate.value as number))
    .map((candidate) => `「${candidate.label}」`)
    .join(''),
);

const submitBulkUpdateFreeTags = async () => {
  const count = props.selectedCallTargetIds.length;
  const confirmMessageKey = mode.value === 'add' ? 'freeTag.bulkUpdate.confirmAdd' : 'freeTag.bulkUpdate.confirmDelete';
  const confirmed = await confirm({
    message: i18n.t(confirmMessageKey, { count, tags: selectedFreeTagNames.value }),
  });
  if (!confirmed) return;

  let succeeded = false;
  await doActionWithWait('bulkUpdateFreeTagsWait', async () => {
    try {
      const request = {
        bulkFreeTagsBody: {
          callTargetIds: props.selectedCallTargetIds,
          freeTagIds: selectedFreeTagIds.value,
        },
      };
      if (mode.value === 'add') {
        await api.bulkAppendFreeTags({ request });
      } else {
        await api.bulkDeleteFreeTags({ request });
      }
      succeeded = true;
      const doneMessageKey = mode.value === 'add' ? 'freeTag.bulkUpdate.doneAdd' : 'freeTag.bulkUpdate.doneDelete';
      toast.show(i18n.t(doneMessageKey));
    } catch {
      // NOTE: エラーダイアログはApiServiceが表示するため、モーダルは開いたままにする
    }
  });
  if (!succeeded) return;

  handleClose();
  await getCallList();
};

const getCallList = async () => {
  // UX改善のため、データ再読込時に全体ローディングしないようになっている
  toast.show({ message: i18n.t('callList.update.loading'), type: 'info' });
  await doActionWithWait('getCallTargetsBackgroundWait', async () => {
    await store.dispatch('user/getCallTargetsAction');
  });
  toast.show({ message: i18n.t('callList.update.done'), type: 'success' });
};
</script>

<style lang="scss" scoped>
  .modal-body {
    padding: $basespace-600;
  }

  .free-tag-select {
    width: 100%;
  }

  .note {
    color: $textcolor-light;
    font-size: $fontsize-100;
  }
</style>
